import * as Lambda from './LambdaApi';
import { Auth } from 'aws-amplify';
import config from '../config';
import { clearAllGreetingsSummaryCache } from '../neighbors/utils/greetingsSummaryCache';

interface signUpParams {
    registrantId?: string;
    password: string;
    firstName: string;
    lastName: string;
    token?: string;
    receivedContact?: string;
    contact?: string;
}

const checkIfUserIsLoggedin = async () => {
    try {
        // Members (guest) logins can come back from an OAuth redirect with an empty local Amplify
        // cache, so they read fresh attributes from Cognito. Other platforms keep the cached read
        // they always used, which saves a Cognito call on every check.
        const bypassCache = process.env.REACT_APP_PLATFORM === 'members';
        const cognitoAuthSession = await Auth.currentAuthenticatedUser({ bypassCache });
        return cognitoAuthSession

    } catch (err) {
        return false;
    }

}



const cognitoSignUp = async (signUpParams: signUpParams) => {
    const apiPath = '/registrations';
    const signUpResponse = await Lambda.Post(apiPath, signUpParams, 'lambda');
    return signUpResponse.relative;
};

const cognitoSignIn = async (username: string, password: string) => {
    try {
        const user = await Auth.signIn(username, password);
        if (user.attributes['custom:accountType'] === 'relative') {
            localStorage.setItem('haveLoggedInBefore', 'true');
            return user;
        }
        throw new Error('User is not confirmed');
    } catch (error) {
        if (error.message === 'User is not confirmed.') return 'not confirmed';
        throw error;
    }
};

const guestCognitoSignIn = async (username: string, password: string) => {
    const user = await Auth.signIn(username, password);
    localStorage.setItem('haveLoggedInBefore', 'true');
    return user;
};

const guestCognitoSignUp = async ({
    email,
    password,
    firstName,
    lastName,
}: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
}) => {
    return Auth.signUp({
        username: email,
        password,
        attributes: {
            email,
            given_name: firstName,
            family_name: lastName,
            name: `${firstName} ${lastName}`.trim(),
        },
    });
};

const confirmGuestSignUp = async (email: string, code: string) => {
    return Auth.confirmSignUp(email, code);
};

const isGuestSocialAuthConfigured = (): boolean => {
    // Matches src/index.tsx, which only sets up the hosted UI on Members.
    if (process.env.REACT_APP_PLATFORM !== 'members') return false;
    const env = process.env.SERVER_ENV || 'development';
    const envConfig = config[env] || config.development;
    const authDomain = process.env.REACT_APP_AUTH_DOMAIN || envConfig.authDomain;
    const redirectSignIn = process.env.REACT_APP_AUTH_REDIRECT_SIGN_IN || window.location.origin;

    return Boolean(authDomain && redirectSignIn);
};

const rememberGuestSocialLogin = () => {
    try {
        sessionStorage.setItem('speak2NeighborsGuestSocialLogin', 'true');
    } catch (error) {
        console.log(error);
    }
};

const clearGuestSocialLogin = () => {
    try {
        sessionStorage.removeItem('speak2NeighborsGuestSocialLogin');
    } catch (error) {
        console.log(error);
    }
};

const hasGuestSocialLoginInProgress = (): boolean => {
    try {
        return sessionStorage.getItem('speak2NeighborsGuestSocialLogin') === 'true';
    } catch (error) {
        console.log(error);
        return false;
    }
};

const guestFederatedSignIn = async (provider: string) => {
    if (!isGuestSocialAuthConfigured()) {
        throw new Error('Social login is not configured for this environment.');
    }

    rememberGuestSocialLogin();
    return Auth.federatedSignIn({
        provider: provider as any,
    });
};

const cognitoSignOut = async (): Promise<any> => {
    // Cleared first so it goes even if Cognito's sign-out then fails.
    clearAllGreetingsSummaryCache();
    try {
        return await Auth.signOut();
    } catch (error) {
        throw Error(error);
    }
};

const ConfirmToken = async (token, registrantId) => {
    try {
        const apiPath = `/registrations/invite-tokens/${token}`;
        const payloadObject = {
            queryStringParameters: {
                registrantId,
            },
        };
        const confirmTokenResponse = await Lambda.Get(apiPath, payloadObject, 'lambda');
        return confirmTokenResponse.isTokenValid;
    } catch (error) {
        throw Error(error);
    }
};

const resendConfirmationCode = async (username) => {
    try {
        return await Auth.resendSignUp(username);
    } catch (error) {
        throw Error(error);
    }
};

const forgotPassword = async (username) => {
    try {
        const result = await Auth.forgotPassword(username);
        console.log(result);
        return true;
    } catch (e) {
        console.log('er', e);
        return false;
    }
};

const setForgottenPassword = async (username, code, newPassword) => {
    try {
        const result = await Auth.forgotPasswordSubmit(username, code, newPassword);

        return true;
    } catch (e) {
        console.log(e);

        return false;
    }
};

const resetPassword = async (oldPassword, newPassword) => {
    try {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.changePassword(user, oldPassword, newPassword);
        return true;
    } catch (e) {
        if (['NotAuthorizedException', 'InvalidParameterException'].includes(e.code)) {
            return false;
        }
        if (e.code === 'InvalidPasswordException') {
            throw new Error(e.message || 'Password does not meet the required policy.');
        }
        return Promise.reject(e);
    }
};

const checkIfUserExists = async username => {
    try {
        const apiPath = `/registrations/users/${username}`;
        const userCheckResponse = await Lambda.Get(apiPath, {}, 'lambda');
        return userCheckResponse.success;
    } catch (error) {
        console.log(error);
        return false;
    }
};

export {
    cognitoSignUp,
    cognitoSignIn,
    guestCognitoSignIn,
    guestCognitoSignUp,
    confirmGuestSignUp,
    isGuestSocialAuthConfigured,
    rememberGuestSocialLogin,
    clearGuestSocialLogin,
    hasGuestSocialLoginInProgress,
    guestFederatedSignIn,
    cognitoSignOut,
    ConfirmToken,
    resendConfirmationCode,
    forgotPassword,
    setForgottenPassword,
    resetPassword,
    checkIfUserExists,
    checkIfUserIsLoggedin
};
